/* src/pages/PartnerDetail.jsx */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { Helmet, HelmetProvider } from "react-helmet-async";
import {
  FileText,
  FileVideo,
  FileMusic,
  FileImage,
  FileArchive,
  Link2,
  Globe,
} from "lucide-react";
import "./Partner.css";

import * as Advanced from "./PartnerDetailAdvanced";
import { EditPartnerDialog, ShareMenu } from "./PartnerDetailAdvanced";
import { useAuth } from "../user/AuthContext";

console.log("🔍 Advanced exports:", Advanced);

const fileIcon = (name) => {
  const ext = name.split(".").pop().toLowerCase();
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) return FileImage;
  if (["mp4", "mov", "webm"].includes(ext)) return FileVideo;
  if (["mp3", "wav", "ogg"].includes(ext)) return FileMusic;
  if (["zip", "rar", "7z"].includes(ext)) return FileArchive;
  return FileText;
};

export default function PartnerDetail() {
  const { partnerId } = useParams();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    (async () => {
      const snap = await getDoc(doc(db, "partners", partnerId));
      if (snap.exists()) {
        setPartner(snap.data());
      }
      setLoading(false);
    })();
  }, [partnerId]);

  if (loading) return <p style={{ padding: 40 }}>Loading…</p>;
  if (!partner) return <p style={{ padding: 40 }}>Partner not found</p>;

  const {
    name,
    logo,
    description,
    longDesc,
    hashtags = "",
    docLinks = [],
    files = [],
    contact = {},
    seo = {},
  } = partner;

  const tagArr = Array.isArray(hashtags)
    ? hashtags
    : hashtags.split(",").map((s) => s.trim()).filter(Boolean);

  const seoTitle = seo.title || name;
  const seoDesc = seo.desc || description;
  const seoImage = seo.image || logo;
  const seoKeywords = seo.keywords || tagArr.join(",");

  const canEdit = ["admin", "editor"].includes(user?.role);

  return (
    <HelmetProvider>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        {seoImage && <meta property="og:image" content={seoImage} />}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        {seoKeywords && <meta name="keywords" content={seoKeywords} />}
        <script
          type="application/ld+json"
          children={JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name,
            url: window.location.href,
            logo,
          })}
        />
      </Helmet>

      <div className="partner-container">
        <div className="partner-banner">
          <Link to="/partners" className="partner-back">
            ← Back
          </Link>
          {logo && <img src={logo} alt={name} className="partner-logo-lg" />}
          <div className="partner-actions">
            <EditPartnerDialog
              partnerId={partnerId}
              partner={partner}
              canEdit={canEdit}
            />
            <ShareMenu
              url={window.location.href}
              title={name}
              logo={logo}
              partnerId={partnerId}
            />
          </div>
          <div className="partner-banner-content">
            <h1>{name}</h1>
            <p className="partner-tagline">{description}</p>
            {tagArr.length > 0 && (
              <div className="partner-tags">
                {tagArr.map((t) => (
                  <span key={t}>{t}</span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="partner-sections">
          {longDesc && (
            <section className="partner-card">
              <h2>About the Partnership</h2>
              <p>{longDesc}</p>
            </section>
          )}

          {docLinks.length > 0 && (
            <section className="partner-card">
              <h2>Reference Links</h2>
              <ul className="partner-list">
                {docLinks.map((url, i) => (
                  <li key={i}>
                    <Link2 size={18} />{" "}
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      {url.length > 60 ? url.slice(0, 57) + "…" : url}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {files.length > 0 && (
            <section className="partner-card">
              <h2>Files</h2>
              <ul className="partner-list">
                {files.map(({ name: n, url }) => {
                  const Icon = fileIcon(n);
                  return (
                    <li key={n}>
                      <Icon size={18} />{" "}
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        {n}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {(contact.name || contact.email || contact.phone) && (
            <section className="partner-card">
              <h2>Point of Contact</h2>
              {contact.name && (
                <p>
                  <strong>Name:</strong> {contact.name}
                </p>
              )}
              {contact.email && (
                <p>
                  <strong>Email:</strong>{" "}
                  <a href={`mailto:${contact.email}`}>{contact.email}</a>
                </p>
              )}
              {contact.phone && (
                <p>
                  <strong>Phone:</strong> {contact.phone}
                </p>
              )}
              {contact.note && (
                <p style={{ whiteSpace: "pre-wrap" }}>{contact.note}</p>
              )}
            </section>
          )}

          <section className="partner-card">
            <h2>SEO Preview</h2>
            <div className="seo-preview">
              <p className="seo-title">{seoTitle}</p>
              <p className="seo-url">
                <Globe size={12} /> {window.location.href}
              </p>
              <p className="seo-desc">{seoDesc}</p>
              {seoKeywords && (
                <p className="seo-keywords">
                  <strong>Keywords:</strong> {seoKeywords}
                </p>
              )}
              {seoImage && (
                <div className="seo-image-wrapper">
                  <img src={seoImage} alt="SEO Cover" />
                </div>
              )}
            </div>
            <p style={{ fontSize: 12, opacity: 0.7, marginTop: 12 }}>
              We automatically add these meta tags & Open Graph data to boost
              your visibility on Google and social platforms.
            </p>
          </section>
        </div>
      </div>
    </HelmetProvider>
  );
}
